import React, { useState, useEffect, useCallback } from 'react';
import { 
  CreditCard, BarChart3, Users, Terminal, Settings, LogOut, 
  Plus, Search, RefreshCw, Eye, EyeOff, Shield,
  ArrowUpRight, ArrowDownLeft, TrendingUp, DollarSign,
  ChevronRight, UserCog, Store, AlertTriangle, CheckCircle,
  XCircle, Clock, Edit, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Status colors
const STATUS_COLORS = {
  active: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Active' },
  pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Pending' },
  suspended: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Suspended' },
  closed: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'Closed' }
};

const MerchantAdmin = () => {
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('merchant_admin_token'));
  
  // UI state
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  
  // Data state
  const [analytics, setAnalytics] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [topMerchants, setTopMerchants] = useState([]);
  
  // Modal state
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [showMerchantModal, setShowMerchantModal] = useState(false);
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  
  // Auth form state
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });

  const getAuthHeader = useCallback(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  // Check authentication on mount
  useEffect(() => {
    if (token) {
      checkAuth();
    } else {
      setIsLoading(false);
    }
  }, [token]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/api/merchant/admin/me`, {
        headers: getAuthHeader()
      });
      setAdmin(response.data);
      setIsAuthenticated(true);
      loadDashboardData();
    } catch (error) {
      localStorage.removeItem('merchant_admin_token');
      setToken(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const loadDashboardData = async () => {
    try {
      const [analyticsRes, merchantsRes, transactionsRes, topRes] = await Promise.all([
        axios.get(`${API}/api/merchant/admin/analytics/overview?period=month`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/merchant/admin/merchants?limit=50`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/merchant/admin/transactions?limit=20`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/merchant/admin/analytics/top-merchants?limit=5`, { headers: getAuthHeader() })
      ]);
      
      setAnalytics(analyticsRes.data);
      setMerchants(merchantsRes.data.merchants || []);
      setTransactions(transactionsRes.data.transactions || []);
      setTopMerchants(topRes.data.top_merchants || []);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error('Failed to load dashboard data');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/api/merchant/admin/login`, loginForm);
      
      localStorage.setItem('merchant_admin_token', response.data.token);
      setToken(response.data.token);
      setAdmin(response.data.admin);
      setIsAuthenticated(true);
      toast.success('Welcome back, Admin!');
      loadDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('merchant_admin_token');
    setToken(null);
    setIsAuthenticated(false);
    setAdmin(null);
    toast.success('Logged out');
  };

  const initAdmin = async () => {
    try {
      const response = await axios.post(`${API}/api/merchant/admin/init`);
      toast.success('Admin initialized! Check credentials below.');
      alert(`Default Admin Created!\n\nEmail: ${response.data.email}\nPassword: ${response.data.password}\n\nPlease change this password immediately!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initialize admin');
    }
  };

  const updateMerchantStatus = async (merchantId, status, reason = '') => {
    try {
      await axios.put(
        `${API}/api/merchant/admin/merchants/${merchantId}/status`,
        { status, reason },
        { headers: getAuthHeader() }
      );
      toast.success(`Merchant ${status}`);
      loadDashboardData();
      setShowMerchantModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const updateMerchantFees = async (merchantId, percentage, fixed) => {
    try {
      await axios.put(
        `${API}/api/merchant/admin/merchants/${merchantId}/fees`,
        { merchant_id: merchantId, percentage, fixed },
        { headers: getAuthHeader() }
      );
      toast.success('Fees updated');
      loadDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update fees');
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-white/60">Loading...</p>
        </div>
      </div>
    );
  }

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-950 via-red-950/20 to-gray-950 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-600 to-orange-600 flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-white">Onerik</span>
            </div>
            <p className="text-white/60">Merchant Admin Portal</p>
          </div>

          {/* Login Card */}
          <div className="bg-black/40 border border-white/10 rounded-2xl p-6">
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="text-sm text-white/60 mb-1 block">Admin Email</label>
                <input
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({...loginForm, email: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 rounded-xl h-12 px-4 text-white placeholder:text-white/40 focus:outline-none focus:border-red-500"
                  placeholder="admin@onerik.com"
                  required
                />
              </div>
              <div>
                <label className="text-sm text-white/60 mb-1 block">Password</label>
                <input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 rounded-xl h-12 px-4 text-white placeholder:text-white/40 focus:outline-none focus:border-red-500"
                  placeholder="••••••••"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-red-600 to-orange-600 text-white font-medium py-3 rounded-xl hover:opacity-90 transition-all"
              >
                Sign In as Admin
              </button>
            </form>

            {/* Init Admin Button */}
            <div className="mt-6 pt-6 border-t border-white/10">
              <p className="text-xs text-white/40 mb-3 text-center">First time setup?</p>
              <button
                onClick={initAdmin}
                className="w-full py-2 bg-white/5 text-white/60 rounded-xl hover:bg-white/10 transition-all text-sm"
              >
                Initialize Default Admin
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main Admin Dashboard
  return (
    <div className="min-h-screen bg-gray-950 flex">
      {/* Sidebar */}
      <div className="w-64 bg-black/40 border-r border-white/10 p-4 flex flex-col">
        {/* Logo */}
        <div className="flex items-center gap-2 mb-8 px-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-red-600 to-orange-600 flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-bold text-white">Onerik</span>
          <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded ml-auto">Admin</span>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1">
          {[
            { id: 'overview', icon: BarChart3, label: 'Overview' },
            { id: 'merchants', icon: Store, label: 'Merchants' },
            { id: 'transactions', icon: ArrowUpRight, label: 'Transactions' },
            { id: 'terminals', icon: Terminal, label: 'Terminals' },
            { id: 'admins', icon: UserCog, label: 'Admin Users' },
            { id: 'settings', icon: Settings, label: 'Settings' },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                activeTab === item.id 
                  ? 'bg-red-600 text-white' 
                  : 'text-white/60 hover:bg-white/5 hover:text-white'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </button>
          ))}
        </nav>

        {/* Admin User */}
        <div className="pt-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 mb-4">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-600 to-orange-600 flex items-center justify-center text-white font-bold">
              {admin?.name?.charAt(0) || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{admin?.name}</p>
              <p className="text-xs text-white/40 truncate">{admin?.role}</p>
            </div>
          </div>
          <button 
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-white/60 hover:bg-white/5 hover:text-white transition-all"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Sign Out</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-6 overflow-auto">
        {activeTab === 'overview' && (
          <OverviewTab 
            analytics={analytics} 
            topMerchants={topMerchants}
            transactions={transactions}
            onRefresh={loadDashboardData}
          />
        )}
        
        {activeTab === 'merchants' && (
          <MerchantsTab 
            merchants={merchants}
            onSelectMerchant={(m) => { setSelectedMerchant(m); setShowMerchantModal(true); }}
            onRefresh={loadDashboardData}
            getAuthHeader={getAuthHeader}
          />
        )}
        
        {activeTab === 'transactions' && (
          <TransactionsTab 
            transactions={transactions}
            getAuthHeader={getAuthHeader}
          />
        )}
        
        {activeTab === 'terminals' && (
          <TerminalsTab getAuthHeader={getAuthHeader} />
        )}
        
        {activeTab === 'admins' && (
          <AdminsTab 
            admin={admin}
            getAuthHeader={getAuthHeader}
            onCreateAdmin={() => setShowCreateAdmin(true)}
          />
        )}
        
        {activeTab === 'settings' && (
          <SettingsTab 
            admin={admin}
            getAuthHeader={getAuthHeader}
          />
        )}
      </div>

      {/* Merchant Detail Modal */}
      {showMerchantModal && selectedMerchant && (
        <MerchantDetailModal
          merchant={selectedMerchant}
          onClose={() => setShowMerchantModal(false)}
          onUpdateStatus={updateMerchantStatus}
          onUpdateFees={updateMerchantFees}
          getAuthHeader={getAuthHeader}
        />
      )}

      {/* Create Admin Modal */}
      {showCreateAdmin && (
        <CreateAdminModal
          onClose={() => setShowCreateAdmin(false)}
          getAuthHeader={getAuthHeader}
          onSuccess={() => { setShowCreateAdmin(false); toast.success('Admin created'); }}
        />
      )}
    </div>
  );
};

// Overview Tab
const OverviewTab = ({ analytics, topMerchants, transactions, onRefresh }) => {
  const stats = [
    { 
      label: 'Total Volume', 
      value: `€${(analytics?.transaction_summary?.gross_volume || 0).toLocaleString()}`,
      icon: DollarSign,
      color: 'purple'
    },
    { 
      label: 'Transactions', 
      value: analytics?.transaction_summary?.total_transactions || 0,
      icon: ArrowUpRight,
      color: 'blue'
    },
    { 
      label: 'Active Merchants', 
      value: analytics?.merchants?.active || 0,
      icon: Store,
      color: 'green'
    },
    { 
      label: 'Platform Fees', 
      value: `€${(analytics?.transaction_summary?.total_fees || 0).toLocaleString()}`,
      icon: TrendingUp,
      color: 'orange'
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Admin Overview</h1>
          <p className="text-white/60">Platform-wide analytics and metrics</p>
        </div>
        <button 
          onClick={onRefresh}
          className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white hover:bg-white/10 transition-all"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-4">
        {stats.map((stat, i) => (
          <div key={i} className="bg-black/40 border border-white/10 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div className={`w-10 h-10 rounded-xl bg-${stat.color}-500/20 flex items-center justify-center`}>
                <stat.icon className={`w-5 h-5 text-${stat.color}-400`} />
              </div>
            </div>
            <p className="text-2xl font-bold text-white mb-1">{stat.value}</p>
            <p className="text-sm text-white/60">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Top Merchants & Recent Transactions */}
      <div className="grid grid-cols-2 gap-6">
        {/* Top Merchants */}
        <div className="bg-black/40 border border-white/10 rounded-2xl p-5">
          <h2 className="font-semibold text-white mb-4">Top Merchants</h2>
          <div className="space-y-3">
            {topMerchants.map((m, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white text-sm font-bold">
                    {i + 1}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{m.business_name}</p>
                    <p className="text-xs text-white/40">{m.total_transactions} txns</p>
                  </div>
                </div>
                <p className="text-sm font-medium text-white">€{m.total_volume?.toLocaleString()}</p>
              </div>
            ))}
            {topMerchants.length === 0 && (
              <p className="text-center text-white/40 py-4">No data yet</p>
            )}
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="bg-black/40 border border-white/10 rounded-2xl p-5">
          <h2 className="font-semibold text-white mb-4">Recent Transactions</h2>
          <div className="space-y-3">
            {transactions.slice(0, 5).map((txn, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    txn.status === 'completed' ? 'bg-green-500/20' : 'bg-red-500/20'
                  }`}>
                    {txn.status === 'completed' ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-400" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">•••• {txn.card_last_four}</p>
                    <p className="text-xs text-white/40">{txn.merchant_id?.slice(0, 12)}...</p>
                  </div>
                </div>
                <p className="text-sm font-medium text-white">{txn.currency} {txn.amount?.toFixed(2)}</p>
              </div>
            ))}
            {transactions.length === 0 && (
              <p className="text-center text-white/40 py-4">No transactions yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Merchant Status Breakdown */}
      <div className="bg-black/40 border border-white/10 rounded-2xl p-5">
        <h2 className="font-semibold text-white mb-4">Merchant Status</h2>
        <div className="grid grid-cols-4 gap-4">
          <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
            <p className="text-2xl font-bold text-green-400">{analytics?.merchants?.active || 0}</p>
            <p className="text-sm text-green-400/80">Active</p>
          </div>
          <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-xl">
            <p className="text-2xl font-bold text-yellow-400">{analytics?.merchants?.pending || 0}</p>
            <p className="text-sm text-yellow-400/80">Pending</p>
          </div>
          <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
            <p className="text-2xl font-bold text-blue-400">{analytics?.terminals?.total || 0}</p>
            <p className="text-sm text-blue-400/80">Terminals</p>
          </div>
          <div className="p-4 bg-purple-500/10 border border-purple-500/20 rounded-xl">
            <p className="text-2xl font-bold text-purple-400">{analytics?.transaction_summary?.success_rate || 0}%</p>
            <p className="text-sm text-purple-400/80">Success Rate</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Merchants Tab
const MerchantsTab = ({ merchants, onSelectMerchant, onRefresh, getAuthHeader }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const filteredMerchants = merchants.filter(m => {
    const matchesSearch = !searchTerm || 
      m.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.business_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.merchant_id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || m.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Merchants</h1>
        <button 
          onClick={onRefresh}
          className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white hover:bg-white/10 transition-all"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search merchants..."
            className="w-full bg-white/5 border border-white/10 rounded-xl h-12 pl-12 pr-4 text-white placeholder:text-white/40 focus:outline-none focus:border-red-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-white/5 border border-white/10 rounded-xl h-12 px-4 text-white focus:outline-none focus:border-red-500"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="suspended">Suspended</option>
          <option value="closed">Closed</option>
        </select>
      </div>

      {/* Merchants Table */}
      <div className="bg-black/40 border border-white/10 rounded-2xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-white/5">
            <tr>
              <th className="text-left text-white/60 text-sm font-medium p-4">Merchant</th>
              <th className="text-left text-white/60 text-sm font-medium p-4">Type</th>
              <th className="text-left text-white/60 text-sm font-medium p-4">Status</th>
              <th className="text-left text-white/60 text-sm font-medium p-4">Fees</th>
              <th className="text-left text-white/60 text-sm font-medium p-4">Created</th>
              <th className="text-left text-white/60 text-sm font-medium p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredMerchants.map((m, i) => (
              <tr key={i} className="border-t border-white/5 hover:bg-white/5">
                <td className="p-4">
                  <div>
                    <p className="text-white font-medium">{m.business_name}</p>
                    <p className="text-white/40 text-sm">{m.business_email}</p>
                  </div>
                </td>
                <td className="p-4 text-white/60">{m.business_type}</td>
                <td className="p-4">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${STATUS_COLORS[m.status]?.bg} ${STATUS_COLORS[m.status]?.text}`}>
                    {STATUS_COLORS[m.status]?.label}
                  </span>
                </td>
                <td className="p-4 text-white/60">{m.fee_config?.percentage}% + €{m.fee_config?.fixed}</td>
                <td className="p-4 text-white/40 text-sm">{new Date(m.created_at).toLocaleDateString()}</td>
                <td className="p-4">
                  <button 
                    onClick={() => onSelectMerchant(m)}
                    className="px-3 py-1.5 bg-white/5 text-white rounded-lg hover:bg-white/10 transition-all text-sm"
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredMerchants.length === 0 && (
          <div className="p-8 text-center text-white/40">
            No merchants found
          </div>
        )}
      </div>
    </div>
  );
};

// Transactions Tab
const TransactionsTab = ({ transactions, getAuthHeader }) => (
  <div className="space-y-6">
    <h1 className="text-2xl font-bold text-white">All Transactions</h1>
    <div className="bg-black/40 border border-white/10 rounded-2xl overflow-hidden">
      <table className="w-full">
        <thead className="bg-white/5">
          <tr>
            <th className="text-left text-white/60 text-sm font-medium p-4">Transaction ID</th>
            <th className="text-left text-white/60 text-sm font-medium p-4">Merchant</th>
            <th className="text-left text-white/60 text-sm font-medium p-4">Amount</th>
            <th className="text-left text-white/60 text-sm font-medium p-4">Fee</th>
            <th className="text-left text-white/60 text-sm font-medium p-4">Status</th>
            <th className="text-left text-white/60 text-sm font-medium p-4">Date</th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((txn, i) => (
            <tr key={i} className="border-t border-white/5">
              <td className="p-4 text-white font-mono text-sm">{txn.transaction_id?.slice(0, 20)}...</td>
              <td className="p-4 text-white/60 text-sm">{txn.merchant_id?.slice(0, 16)}...</td>
              <td className="p-4 text-white">{txn.currency} {txn.amount?.toFixed(2)}</td>
              <td className="p-4 text-white/60">{txn.currency} {txn.fee_amount?.toFixed(2)}</td>
              <td className="p-4">
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  txn.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                }`}>
                  {txn.status}
                </span>
              </td>
              <td className="p-4 text-white/40 text-sm">{new Date(txn.created_at).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Terminals Tab
const TerminalsTab = ({ getAuthHeader }) => (
  <div className="space-y-6">
    <h1 className="text-2xl font-bold text-white">All Terminals</h1>
    <div className="bg-black/40 border border-white/10 rounded-2xl p-8 text-center">
      <Terminal className="w-12 h-12 mx-auto mb-4 text-white/20" />
      <p className="text-white/60">Terminal management view coming soon</p>
    </div>
  </div>
);

// Admins Tab
const AdminsTab = ({ admin, getAuthHeader, onCreateAdmin }) => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <h1 className="text-2xl font-bold text-white">Admin Users</h1>
      {admin?.role === 'super_admin' && (
        <button 
          onClick={onCreateAdmin}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all"
        >
          <Plus className="w-4 h-4" />
          Add Admin
        </button>
      )}
    </div>
    <div className="bg-black/40 border border-white/10 rounded-2xl p-5">
      <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-600 to-orange-600 flex items-center justify-center text-white font-bold text-lg">
          {admin?.name?.charAt(0)}
        </div>
        <div>
          <p className="font-medium text-white">{admin?.name}</p>
          <p className="text-sm text-white/40">{admin?.email}</p>
        </div>
        <span className="ml-auto px-3 py-1 bg-red-500/20 text-red-400 rounded-lg text-sm font-medium">
          {admin?.role}
        </span>
      </div>
    </div>
  </div>
);

// Settings Tab
const SettingsTab = ({ admin, getAuthHeader }) => (
  <div className="space-y-6">
    <h1 className="text-2xl font-bold text-white">Platform Settings</h1>
    <div className="bg-black/40 border border-white/10 rounded-2xl p-6">
      <h2 className="font-semibold text-white mb-4">Default Fee Configuration</h2>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm text-white/60 mb-2 block">Percentage Fee (%)</label>
          <input
            type="number"
            defaultValue="2.9"
            step="0.1"
            className="w-full bg-white/5 border border-white/10 rounded-xl h-12 px-4 text-white focus:outline-none focus:border-red-500"
          />
        </div>
        <div>
          <label className="text-sm text-white/60 mb-2 block">Fixed Fee (€)</label>
          <input
            type="number"
            defaultValue="0.30"
            step="0.01"
            className="w-full bg-white/5 border border-white/10 rounded-xl h-12 px-4 text-white focus:outline-none focus:border-red-500"
          />
        </div>
      </div>
      <button className="mt-4 px-6 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all">
        Save Settings
      </button>
    </div>
  </div>
);

// Merchant Detail Modal
const MerchantDetailModal = ({ merchant, onClose, onUpdateStatus, onUpdateFees, getAuthHeader }) => {
  const [newFeePercentage, setNewFeePercentage] = useState(merchant.fee_config?.percentage || 2.9);
  const [newFeeFixed, setNewFeeFixed] = useState(merchant.fee_config?.fixed || 0.30);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-white">{merchant.business_name}</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white">✕</button>
        </div>

        {/* Merchant Info */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <p className="text-sm text-white/60">Merchant ID</p>
            <p className="text-white font-mono text-sm">{merchant.merchant_id}</p>
          </div>
          <div>
            <p className="text-sm text-white/60">Email</p>
            <p className="text-white">{merchant.business_email}</p>
          </div>
          <div>
            <p className="text-sm text-white/60">Business Type</p>
            <p className="text-white capitalize">{merchant.business_type}</p>
          </div>
          <div>
            <p className="text-sm text-white/60">Country</p>
            <p className="text-white">{merchant.country}</p>
          </div>
          <div>
            <p className="text-sm text-white/60">Status</p>
            <span className={`px-2 py-1 rounded text-xs font-medium ${STATUS_COLORS[merchant.status]?.bg} ${STATUS_COLORS[merchant.status]?.text}`}>
              {STATUS_COLORS[merchant.status]?.label}
            </span>
          </div>
          <div>
            <p className="text-sm text-white/60">Created</p>
            <p className="text-white">{new Date(merchant.created_at).toLocaleDateString()}</p>
          </div>
        </div>

        {/* Fee Configuration */}
        <div className="border-t border-white/10 pt-6 mb-6">
          <h4 className="font-medium text-white mb-4">Fee Configuration</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-white/60 mb-1 block">Percentage (%)</label>
              <input
                type="number"
                value={newFeePercentage}
                onChange={(e) => setNewFeePercentage(parseFloat(e.target.value))}
                step="0.1"
                className="w-full bg-white/5 border border-white/10 rounded-xl h-10 px-4 text-white focus:outline-none focus:border-red-500"
              />
            </div>
            <div>
              <label className="text-sm text-white/60 mb-1 block">Fixed (€)</label>
              <input
                type="number"
                value={newFeeFixed}
                onChange={(e) => setNewFeeFixed(parseFloat(e.target.value))}
                step="0.01"
                className="w-full bg-white/5 border border-white/10 rounded-xl h-10 px-4 text-white focus:outline-none focus:border-red-500"
              />
            </div>
          </div>
          <button 
            onClick={() => onUpdateFees(merchant.merchant_id, newFeePercentage, newFeeFixed)}
            className="mt-3 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all text-sm"
          >
            Update Fees
          </button>
        </div>

        {/* Status Actions */}
        <div className="border-t border-white/10 pt-6">
          <h4 className="font-medium text-white mb-4">Status Actions</h4>
          <div className="flex flex-wrap gap-2">
            {merchant.status !== 'active' && (
              <button 
                onClick={() => onUpdateStatus(merchant.merchant_id, 'active')}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all text-sm flex items-center gap-2"
              >
                <CheckCircle className="w-4 h-4" /> Activate
              </button>
            )}
            {merchant.status !== 'suspended' && (
              <button 
                onClick={() => onUpdateStatus(merchant.merchant_id, 'suspended', 'Admin action')}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-all text-sm flex items-center gap-2"
              >
                <AlertTriangle className="w-4 h-4" /> Suspend
              </button>
            )}
            {merchant.status !== 'closed' && (
              <button 
                onClick={() => onUpdateStatus(merchant.merchant_id, 'closed', 'Admin action')}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all text-sm flex items-center gap-2"
              >
                <XCircle className="w-4 h-4" /> Close Account
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Create Admin Modal
const CreateAdminModal = ({ onClose, getAuthHeader, onSuccess }) => {
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'admin' });
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = async () => {
    if (!form.email || !form.password || !form.name) {
      toast.error('Please fill all fields');
      return;
    }
    
    setIsCreating(true);
    try {
      await axios.post(`${API}/api/merchant/admin/create`, form, { headers: getAuthHeader() });
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create admin');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-md">
        <h3 className="text-lg font-bold text-white mb-4">Create New Admin</h3>
        <div className="space-y-4">
          <div>
            <label className="text-sm text-white/60 mb-1 block">Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({...form, name: e.target.value})}
              className="w-full bg-white/5 border border-white/10 rounded-xl h-12 px-4 text-white focus:outline-none focus:border-red-500"
            />
          </div>
          <div>
            <label className="text-sm text-white/60 mb-1 block">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({...form, email: e.target.value})}
              className="w-full bg-white/5 border border-white/10 rounded-xl h-12 px-4 text-white focus:outline-none focus:border-red-500"
            />
          </div>
          <div>
            <label className="text-sm text-white/60 mb-1 block">Password</label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm({...form, password: e.target.value})}
              className="w-full bg-white/5 border border-white/10 rounded-xl h-12 px-4 text-white focus:outline-none focus:border-red-500"
            />
          </div>
          <div>
            <label className="text-sm text-white/60 mb-1 block">Role</label>
            <select
              value={form.role}
              onChange={(e) => setForm({...form, role: e.target.value})}
              className="w-full bg-white/5 border border-white/10 rounded-xl h-12 px-4 text-white focus:outline-none focus:border-red-500"
            >
              <option value="admin">Admin</option>
              <option value="super_admin">Super Admin</option>
            </select>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 py-3 bg-white/5 text-white rounded-xl hover:bg-white/10 transition-all">
            Cancel
          </button>
          <button 
            onClick={handleCreate}
            disabled={isCreating}
            className="flex-1 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all disabled:opacity-50"
          >
            {isCreating ? 'Creating...' : 'Create Admin'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MerchantAdmin;
